import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../config/authConfig';
import { mockAuthService } from './mockAuthService';

export interface AuthUser {
  userId: string;
  email: string;
  name: string;
  displayName: string;
  department: string;
  role: string;
  tenantId: string | null;
  lastLoginDate: string;
  isActive: boolean;
  /** True when authenticated but not yet assigned to any tenant. Shows Pending Setup screen. */
  isPendingSetup: boolean;
}

class AuthService {
  private msalInstance: PublicClientApplication;
  private currentAccount: AccountInfo | null = null;
  private initPromise: Promise<void>;

  constructor() {
    this.msalInstance = new PublicClientApplication(msalConfig);
    this.initPromise = this.initializeAccount();
  }

  private async initializeAccount() {
    await this.msalInstance.initialize();

    // Handle redirect response first
    try {
      const response = await this.msalInstance.handleRedirectPromise();
      if (response) {
        this.currentAccount = response.account;
      }
    } catch (_error) {
      // Silent error handling for redirect response
    }

    // If no redirect response, check for existing accounts
    if (!this.currentAccount) {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.currentAccount = accounts[0];
      }
    }
  }

  // Add a method to refresh authentication state
  async refreshAuthenticationState(): Promise<boolean> {
    await this.initializeAccount();
    return this.isAuthenticated();
  }

  async login(loginHint?: string): Promise<boolean> {
    try {
      // Use redirect flow instead of popup for better compatibility
      const request = loginHint ? { ...loginRequest, loginHint } : loginRequest;
      await this.msalInstance.loginRedirect(request);
      return true; // This will redirect, so we won't reach this line
    } catch (_error) {
      return false;
    }
  }

  /** Last access token seen, used only to notice when MSAL has renewed it. */
  private lastSeenToken: string | null = null;

  /** Suppresses token-renewal reporting for the token logout() acquires to report itself. */
  private isSigningOut = false;

  /**
   * Reports a session event to the audit trail (CJIS AU-2). Sign-out and token renewal happen
   * between MSAL and Entra ID without the API being involved, so the server cannot observe them
   * on its own. Takes the token explicitly — calling getToken() here would recurse.
   */
  private async reportSessionEvent(path: string, token: string): Promise<void> {
    try {
      await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}${path}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal: AbortSignal.timeout(3000),
      });
    } catch (_err) {
      // Best-effort by nature: this is a client reporting something only it can see. A missed
      // report costs one session record, and must never block sign-out or token acquisition.
    }
  }

  async getToken(): Promise<string | null> {
    // Wait for initialization to complete
    await this.initPromise;

    if (!this.currentAccount) {
      return null;
    }

    try {
      // Try to get token for API access first
      const { apiRequest } = await import('../config/authConfig');
      const response = await this.msalInstance.acquireTokenSilent({
        ...apiRequest,
        account: this.currentAccount,
      });

      // A different token than last time means MSAL renewed the session. The first token of a
      // session is not a renewal — that one is recorded server-side as the login by /api/auth/me.
      // Skipped while signing out: logout() acquires a token to authenticate its own report, and a
      // TOKEN_REFRESH recorded immediately before the LOGOUT it enabled is noise, not an event.
      if (!this.isSigningOut && this.lastSeenToken && this.lastSeenToken !== response.accessToken) {
        void this.reportSessionEvent('/api/auth/token-refresh', response.accessToken);
      }
      this.lastSeenToken = response.accessToken;

      return response.accessToken;
    } catch (apiError) {
      console.error('Failed to acquire token silently:', apiError);
      // For redirect flow, we can't return a token immediately
      // The page will redirect and the token will be available after redirect
      try {
        const { apiRequest } = await import('../config/authConfig');
        await this.msalInstance.acquireTokenRedirect(apiRequest);
        // This will redirect the page, so we won't reach this line
        return null;
      } catch (redirectError) {
        console.error('Failed to acquire token via redirect:', redirectError);
        return null;
      }
    }
  }

  // Cache for DB-sourced role and tenant from /api/auth/me
  private cachedRole: string | null = null;
  private cachedTenantId: string | null = null;
  private cachedPendingSetup: boolean = false;
  private rolePromise: Promise<string> | null = null;

  /// Fetches the user's actual role and tenant from the backend (DB source of truth).
  /// Cached for the session lifetime — only one API call per page load.
  private async fetchRoleFromApi(): Promise<string> {
    if (this.cachedRole) return this.cachedRole;
    if (this.rolePromise) return this.rolePromise;

    this.rolePromise = (async () => {
      try {
        const token = await this.getToken();
        if (!token) return 'Agent';
        const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || ''}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          this.cachedRole = data.role || 'Agent';
          this.cachedTenantId = data.tenantId ?? null;
          // No tenant + role fell back to 'Agent' from backend → user not yet assigned
          this.cachedPendingSetup = !data.tenantId && data.role === 'Agent';
          return this.cachedRole!;
        }
      } catch (_err) {
        // Silent — fall back to Agent
      }
      return 'Agent';
    })();

    return this.rolePromise;
  }

  getUser(): AuthUser | null {
    if (!this.currentAccount) {
      return null;
    }

    const idTokenClaims = this.currentAccount.idTokenClaims as Record<string, unknown> | undefined;

    // Use cached DB role if available, otherwise trigger async fetch.
    // Callers that need the real role should call getUserAsync() instead.
    const role = this.cachedRole
              || (idTokenClaims?.roles as string[] | undefined)?.[0]
              || (idTokenClaims?.role as string | undefined)
              || 'Agent';

    return {
      userId: this.currentAccount.localAccountId,
      email: this.currentAccount.username,
      name: this.currentAccount.name || this.currentAccount.username,
      displayName: this.currentAccount.name || this.currentAccount.username,
      department: (idTokenClaims?.department as string | undefined) || '',
      role: role,
      tenantId: this.cachedTenantId,
      lastLoginDate: new Date().toISOString(),
      isActive: true,
      isPendingSetup: this.cachedPendingSetup,
    };
  }

  /// Returns user with DB-sourced role. Use this in components that gate on role.
  async getUserAsync(): Promise<AuthUser | null> {
    await this.initPromise;
    if (!this.currentAccount) return null;
    await this.fetchRoleFromApi();
    return this.getUser();
  }

  isAuthenticated(): boolean {
    return !!this.currentAccount;
  }

  async logout(): Promise<void> {
    if (this.currentAccount) {
      // Awaited, not fired off: logoutRedirect navigates away and would cancel an in-flight
      // request. Bounded by the timeout in reportSessionEvent so a slow API cannot trap the user
      // in a session they asked to end.
      this.isSigningOut = true;
      try {
        const token = await this.getToken();
        if (token) {
          await this.reportSessionEvent('/api/auth/logout', token);
        }
      } finally {
        this.isSigningOut = false;
      }

      await this.msalInstance.logoutRedirect({
        account: this.currentAccount,
      });
      this.currentAccount = null;
    }
  }

  async ensureAuthenticated(): Promise<boolean> {
    if (this.isAuthenticated()) {
      return true;
    }

    return await this.login();
  }
}

// Common interface for all auth services
export interface IAuthService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  login(...args: any[]): Promise<boolean | { success: boolean; error?: string }>;
  getToken(): Promise<string | null>;
  getUser(): AuthUser | null;
  getUserAsync(): Promise<AuthUser | null>;
  isAuthenticated(): boolean;
  logout(): Promise<void>;
  ensureAuthenticated(): Promise<boolean>;
  refreshAuthenticationState(): Promise<boolean>;
}

// Wrapper to normalize different auth service interfaces
class AuthServiceWrapper implements IAuthService {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  constructor(private service: any) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async login(...args: any[]): Promise<boolean> {
    const result = await this.service.login(...args);
    // Handle different return types
    if (typeof result === 'boolean') {
      return result;
    }
    if (typeof result === 'object' && 'success' in result) {
      return result.success;
    }
    return false;
  }

  async getToken(): Promise<string | null> {
    return this.service.getToken();
  }

  getUser(): AuthUser | null {
    return this.service.getUser();
  }

  async getUserAsync(): Promise<AuthUser | null> {
    if (this.service.getUserAsync) {
      return this.service.getUserAsync();
    }
    return this.service.getUser();
  }

  isAuthenticated(): boolean {
    return this.service.isAuthenticated();
  }

  async logout(): Promise<void> {
    return this.service.logout();
  }

  async ensureAuthenticated(): Promise<boolean> {
    return this.service.ensureAuthenticated();
  }

  async refreshAuthenticationState(): Promise<boolean> {
    return this.service.refreshAuthenticationState();
  }
}

// Use demo auth service for demo environment, mock for local dev, real for production
import { demoAuthService } from './demoAuthService';
import { Config } from '../config';

const isLocalDev = Config.auth.localDev || !Config.auth.enabled;
const isDemoMode = Config.auth.demoMode;

const rawService = isLocalDev ? mockAuthService : isDemoMode ? demoAuthService : new AuthService();
export const authService = new AuthServiceWrapper(rawService);
